<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { RuleDimension } from "@/modules/rule_engine/ruleEngineClasses";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import SelectDimensionDialog from "@/modules/rule_engine/components/SelectDimensionDialog.vue";

const { t } = useI18n();
const store = useRuleEnginedStore();

const dimension = defineModel<RuleDimension | null>();

const isDialogVisible = ref(false);

/**
 * A/B tests all share the `ab_test` dimension, so show the name of the test
 * that was pinned onto it rather than the dimension's own title.
 */
const abTestName = computed(
	() =>
		store.ruleEngineState.abTests.find(
			(abTest) => abTest.uuid === dimension.value?.ab_test_uuid
		)?.name ?? t("rule_engine.ab_test.unknown_test")
);

const displayedValue = computed(() => {
	if (!dimension.value) return "";
	if (dimension.value.ab_test_uuid) return abTestName.value;

	return t(`rule_engine.rule_dimensions.${dimension.value.name_clean}.title`);
});

const applyDimension = (selected: RuleDimension) => {
	dimension.value = selected;
};
</script>

<template>
	<v-text-field
		:model-value="displayedValue"
		readonly
		density="compact"
		hide-details="auto"
		placeholder="Select a dimension…"
		append-inner-icon="mdi-menu-down"
		class="dimension-field"
		@click="isDialogVisible = true"
		@keydown.enter="isDialogVisible = true"
	/>

	<select-dimension-dialog
		v-model="isDialogVisible"
		:dimension="dimension"
		@apply="applyDimension"
	/>
</template>

<style scoped lang="scss">
.dimension-field {
	cursor: pointer;

	:deep(input) {
		cursor: pointer;
	}
}
</style>
